import React, { useState, useEffect } from 'react';
import designApi from '@services/designApi.jsx';
import ResourceUploadField from '@components/common/ResourceUploadField.jsx';

const PaymentLoadingSetting = ({ themeId, themePageMaster }) => {
  // 상태 관리
  const [paymentLoadingPage, setPaymentLoadingPage] = useState({
    themePageMaster: themePageMaster,
    languageCode: 'ko',
    messageText: '결제 처리 중입니다...',
    messageColor: '#FFFFFF',
    messagePositionX: 500,
    messagePositionY: 400,
    loadingSpinnerType: 'default',
    loadingSpinnerColor: '#FFFFFF',
    loadingSpinnerSize: 'medium',
    progressBarType: 'none',
    progressBarColor: '#007BFF',
    progressBarBackgroundColor: '#E0E0E0',
    estimatedDuration: 5,
    timeoutRedirectUrl: 'start',
    logoPositionX: 500,
    logoPositionY: 200
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // API 호출로 데이터 가져오기
  useEffect(() => {
    const fetchPaymentLoadingPage = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await designApi.getPaymentLoadingPage(themeId);
        
        setPaymentLoadingPage({
          ...paymentLoadingPage,
          ...data
        });

        console.log("API 호출 성공:", data);
      } catch (err) {
        console.error("API 호출 실패:", err);
        setError(err);
        // 에러 발생 시 기본 데이터 유지
      } finally {
        setIsLoading(false);
      }
    };

    fetchPaymentLoadingPage();
  }, [themeId]);

  // 입력값 변경 핸들러
  const handleInputChange = (field, value) => {
    setPaymentLoadingPage(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 리소스 업로드 처리 함수
  const handleResourceUploaded = (resourceType, resourceId) => {
    setPaymentLoadingPage({
      ...paymentLoadingPage,
      [resourceType]: resourceId
    });
  };

  // 위치 중앙 정렬 핸들러
  const handleCenterAlign = (type) => {
    const centerX = 500;
    const centerY = type === "message" ? 400 : 200;

    if (type === "message") {
      setPaymentLoadingPage({ 
        ...paymentLoadingPage,
        messagePositionX: centerX, 
        messagePositionY: centerY 
      });
    } else if (type === "logo") {
      setPaymentLoadingPage({ 
        ...paymentLoadingPage,
        logoPositionX: centerX, 
        logoPositionY: centerY 
      });
    }
  };

  // 예상 소요 시간 조절 핸들러
  const handleDurationChange = (value) => {
    const duration = Math.max(1, Math.min(60, parseInt(value) || 5));
    setPaymentLoadingPage({
      ...paymentLoadingPage,
      estimatedDuration: duration
    });
  };

  // 저장 이벤트 리스너 (페이지 상위 컴포넌트에서 저장 요청 시 실행)
  useEffect(() => {
    const eventName = `savePageDesign_payment_loading`; // 각 컴포넌트마다 고유한 이벤트 이름

    const saveHandler = async (event) => {
      try {
        // 저장 중임을 표시
        setIsLoading(true);

        // 자체 로직을 사용하여 데이터 저장
        const dataToSave = {
          ...paymentLoadingPage,
          themeId: event.detail.themeId // themeId를 명시적으로 포함
        };
        const isSucceed = await designApi.savePaymentLoadingPage(event.detail.themeId, dataToSave);

        if(isSucceed) {
          // 성공 메시지
          alert("저장되었습니다.");
        } else {
          // 실패 메시지
          alert("저장에 실패했습니다.");
        }

      } catch (error) {
        // 오류 처리
        console.error("저장 실패:", error);
        alert("저장 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    // 이벤트 리스너 등록
    document.addEventListener(eventName, saveHandler);

    // 컴포넌트 언마운트 시 리스너 제거
    return () => {
      document.removeEventListener(eventName, saveHandler);
    };
  }, [paymentLoadingPage, themeId]); // 의존성 배열에 필요한 상태 포함

  return (
    <div className="design-settings">
      {/* 언어 선택 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">언어 선택</h4>
        <div className="option-select-container">
          <select
            className="option-select"
            value={paymentLoadingPage.languageCode || "ko"}
            onChange={(e) =>
              handleInputChange('languageCode', e.target.value)
            }
          >
            <option value="ko">한국어</option>
            <option value="en">영어</option>
            <option value="jp">일본어</option>
            <option value="cn">중국어</option>
          </select>
          <i className="ri-arrow-down-s-line select-arrow"></i>
        </div>
      </div>

      {/* 배경 이미지 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 이미지</h4>
        <ResourceUploadField
          label=""
          resourceId={paymentLoadingPage.backgroundResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("backgroundResourceId", id)
          }
          acceptTypes="image/*"
        />
      </div>

      {/* 배경 음악 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 음악</h4>
        <ResourceUploadField
          label=""
          resourceId={paymentLoadingPage.backgroundMusicResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("backgroundMusicResourceId", id)
          }
          acceptTypes="audio/*"
        />
      </div>

      {/* 로딩 애니메이션 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">로딩 애니메이션</h4>
        <div className="settings-row">
          <div className="settings-label">커스텀 로딩 동영상</div>
          <ResourceUploadField
            label=""
            resourceId={paymentLoadingPage.loadingVideoResourceId}
            onResourceUploaded={(id) =>
              handleResourceUploaded("loadingVideoResourceId", id)
            }
            acceptTypes="video/*"
          />
        </div>
        <div className="settings-row">
          <div className="settings-label">스피너 타입</div>
          <div className="option-select-container">
            <select
              className="option-select"
              value={paymentLoadingPage.loadingSpinnerType || "default"}
              onChange={(e) =>
                handleInputChange('loadingSpinnerType', e.target.value)
              }
            >
              <option value="default">기본 스피너</option>
              <option value="dots">점 애니메이션</option>
              <option value="pulse">펄스 효과</option>
              <option value="circle">원형 스피너</option>
              <option value="custom">커스텀 동영상</option>
            </select>
            <i className="ri-arrow-down-s-line select-arrow"></i>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">스피너 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={paymentLoadingPage.loadingSpinnerColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={paymentLoadingPage.loadingSpinnerColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('loadingSpinnerColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">스피너 크기</div>
          <div className="option-select-container">
            <select
              className="option-select"
              value={paymentLoadingPage.loadingSpinnerSize || "medium"}
              onChange={(e) =>
                handleInputChange('loadingSpinnerSize', e.target.value)
              }
            >
              <option value="small">작게</option>
              <option value="medium">보통</option>
              <option value="large">크게</option>
            </select>
            <i className="ri-arrow-down-s-line select-arrow"></i>
          </div>
        </div>
      </div>

      {/* 진행률 표시 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">진행률 표시</h4>
        <div className="settings-row">
          <div className="settings-label">진행바 타입</div>
          <div className="option-select-container">
            <select
              className="option-select"
              value={paymentLoadingPage.progressBarType || "none"}
              onChange={(e) =>
                handleInputChange('progressBarType', e.target.value)
              }
            >
              <option value="none">사용 안함</option>
              <option value="linear">선형 진행바</option>
              <option value="circular">원형 진행바</option>
              <option value="percentage">퍼센트 표시</option>
            </select>
            <i className="ri-arrow-down-s-line select-arrow"></i>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">진행바 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={paymentLoadingPage.progressBarColor || "#007BFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={paymentLoadingPage.progressBarColor || "#007BFF"}
              onChange={(e) => handleInputChange('progressBarColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">진행바 배경 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={paymentLoadingPage.progressBarBackgroundColor || "#E0E0E0"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={paymentLoadingPage.progressBarBackgroundColor || "#E0E0E0"}
              onChange={(e) => handleInputChange('progressBarBackgroundColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 메시지 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">로딩 메시지</h4>
        <div className="settings-row">
          <div className="settings-input">
            <input 
              type="text" 
              placeholder="결제 처리 중입니다..." 
              className="text-input"
              value={paymentLoadingPage.messageText || ""}
              onChange={(e) => handleInputChange('messageText', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={paymentLoadingPage.messageColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={paymentLoadingPage.messageColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('messageColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">위치</div>
          <div className="position-component">
            <div className="position-input-group">
              <span className="position-label">X</span>
              <input
                type="text"
                className="position-input"
                value={paymentLoadingPage.messagePositionX || 0}
                onChange={(e) =>
                  setPaymentLoadingPage({
                    ...paymentLoadingPage,
                    messagePositionX: parseInt(e.target.value) || 0,
                  })
                }
              />
              <span className="position-label">Y</span>
              <input
                type="text"
                className="position-input"
                value={paymentLoadingPage.messagePositionY || 0}
                onChange={(e) =>
                  setPaymentLoadingPage({
                    ...paymentLoadingPage,
                    messagePositionY: parseInt(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="direction-buttons">
              <div className="direction-row">
                <button
                  className="direction-btn"
                  data-direction="up"
                  onClick={() =>
                    setPaymentLoadingPage({
                      ...paymentLoadingPage,
                      messagePositionY: (paymentLoadingPage.messagePositionY || 0) - 1,
                    })
                  }
                >
                  <i className="ri-arrow-up-s-fill"></i>
                </button>
              </div>
              <div className="direction-row">
                <button
                  className="direction-btn"
                  data-direction="left"
                  onClick={() =>
                    setPaymentLoadingPage({
                      ...paymentLoadingPage,
                      messagePositionX: (paymentLoadingPage.messagePositionX || 0) - 1,
                    })
                  }
                >
                  <i className="ri-arrow-left-s-fill"></i>
                </button>
                <button
                  className="direction-btn"
                  data-direction="right"
                  onClick={() =>
                    setPaymentLoadingPage({
                      ...paymentLoadingPage,
                      messagePositionX: (paymentLoadingPage.messagePositionX || 0) + 1,
                    })
                  }
                >
                  <i className="ri-arrow-right-s-fill"></i>
                </button>
              </div>
              <div className="direction-row">
                <button
                  className="direction-btn"
                  data-direction="down"
                  onClick={() =>
                    setPaymentLoadingPage({
                      ...paymentLoadingPage,
                      messagePositionY: (paymentLoadingPage.messagePositionY || 0) + 1,
                    })
                  }
                >
                  <i className="ri-arrow-down-s-fill"></i>
                </button>
              </div>
            </div>
            <button
              className="btn center-btn"
              onClick={() => handleCenterAlign("message")}
            >
              중앙정렬
            </button>
          </div>
        </div>
      </div>

      {/* 로고 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">로고</h4>
        <ResourceUploadField
          label=""
          resourceId={paymentLoadingPage.logoResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("logoResourceId", id)
          }
          acceptTypes="image/*"
        />
        <div className="settings-row">
          <div className="settings-label">위치</div>
          <div className="position-component">
            <div className="position-input-group">
              <span className="position-label">X</span>
              <input
                type="text"
                className="position-input"
                value={paymentLoadingPage.logoPositionX || 0}
                onChange={(e) =>
                  setPaymentLoadingPage({
                    ...paymentLoadingPage,
                    logoPositionX: parseInt(e.target.value) || 0,
                  })
                }
              />
              <span className="position-label">Y</span>
              <input
                type="text"
                className="position-input"
                value={paymentLoadingPage.logoPositionY || 0}
                onChange={(e) =>
                  setPaymentLoadingPage({
                    ...paymentLoadingPage,
                    logoPositionY: parseInt(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="direction-buttons">
              <div className="direction-row">
                <button
                  className="direction-btn"
                  data-direction="up"
                  onClick={() =>
                    setPaymentLoadingPage({
                      ...paymentLoadingPage,
                      logoPositionY: (paymentLoadingPage.logoPositionY || 0) - 1,
                    })
                  }
                >
                  <i className="ri-arrow-up-s-fill"></i>
                </button>
              </div>
              <div className="direction-row">
                <button
                  className="direction-btn"
                  data-direction="left"
                  onClick={() =>
                    setPaymentLoadingPage({
                      ...paymentLoadingPage,
                      logoPositionX: (paymentLoadingPage.logoPositionX || 0) - 1,
                    })
                  }
                >
                  <i className="ri-arrow-left-s-fill"></i>
                </button>
                <button
                  className="direction-btn"
                  data-direction="right"
                  onClick={() =>
                    setPaymentLoadingPage({
                      ...paymentLoadingPage,
                      logoPositionX: (paymentLoadingPage.logoPositionX || 0) + 1,
                    })
                  }
                >
                  <i className="ri-arrow-right-s-fill"></i>
                </button>
              </div>
              <div className="direction-row">
                <button
                  className="direction-btn"
                  data-direction="down"
                  onClick={() =>
                    setPaymentLoadingPage({
                      ...paymentLoadingPage,
                      logoPositionY: (paymentLoadingPage.logoPositionY || 0) + 1,
                    })
                  }
                >
                  <i className="ri-arrow-down-s-fill"></i>
                </button>
              </div>
            </div>
            <button
              className="btn center-btn"
              onClick={() => handleCenterAlign("logo")}
            >
              중앙정렬
            </button>
          </div>
        </div>
      </div>

      {/* 시간 설정 섹션 */}
      {/*<div className="settings-box">*/}
      {/*  <h4 className="settings-title">시간 설정</h4>*/}
      {/*  <div className="settings-row">*/}
      {/*    <div className="settings-label">예상 소요 시간</div>*/}
      {/*    <div className="duration-control-container">*/}
      {/*      <button */}
      {/*        className="duration-btn decrease-btn"*/}
      {/*        onClick={() => handleDurationChange((paymentLoadingPage.estimatedDuration || 5) - 1)}*/}
      {/*      >*/}
      {/*        <i className="ri-arrow-down-s-fill"></i>*/}
      {/*      </button>*/}
      {/*      <input */}
      {/*        type="number" */}
      {/*        className="duration-input" */}
      {/*        min="1"*/}
      {/*        max="60"*/}
      {/*        value={paymentLoadingPage.estimatedDuration || 5}*/}
      {/*        onChange={(e) => handleDurationChange(e.target.value)}*/}
      {/*      />*/}
      {/*      <button */}
      {/*        className="duration-btn increase-btn"*/}
      {/*        onClick={() => handleDurationChange((paymentLoadingPage.estimatedDuration || 5) + 1)}*/}
      {/*      >*/}
      {/*        <i className="ri-arrow-up-s-fill"></i>*/}
      {/*      </button>*/}
      {/*      <span className="unit-text">초</span>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*  <div className="settings-row">*/}
      {/*    <div className="settings-label">타임아웃 시 이동할 페이지</div>*/}
      {/*    <div className="option-select-container">*/}
      {/*      <select*/}
      {/*        className="option-select"*/}
      {/*        value={paymentLoadingPage.timeoutRedirectUrl || "start"}*/}
      {/*        onChange={(e) =>*/}
      {/*          handleInputChange('timeoutRedirectUrl', e.target.value)*/}
      {/*        }*/}
      {/*      >*/}
      {/*        <option value="start">시작화면</option>*/}
      {/*        <option value="payment">결제화면</option>*/}
      {/*        <option value="amount">수량선택</option>*/}
      {/*        <option value="error">오류화면</option>*/}
      {/*      </select>*/}
      {/*      <i className="ri-arrow-down-s-line select-arrow"></i>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</div>*/}

      {/* 효과음 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">효과음</h4>
        <ResourceUploadField
          label="로딩 시작 효과음"
          resourceId={paymentLoadingPage.loadingStartSoundResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("loadingStartSoundResourceId", id)
          }
          acceptTypes="audio/*"
        />
        <ResourceUploadField
          label="로딩 완료 효과음"
          resourceId={paymentLoadingPage.loadingCompleteSoundResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("loadingCompleteSoundResourceId", id)
          }
          acceptTypes="audio/*"
        />
      </div>
    </div>
  );
};

export default PaymentLoadingSetting;